import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  Users,
  User,
  Download,
  CalendarRange,
  Loader2,
  ChevronDown,
  // Sparkles, // re-enable with AIVIA Recommends
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";
import { useCurrentTramsContext } from "@/hooks/useTramsAccounts";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { TramsAccountSwitcher } from "@/components/trams/TramsAccountSwitcher";
import {
  SectionErrorBoundary,
  SectionScopeContext,
} from "@/components/invoicing/SectionErrorBoundary";
import type { SectionScopeInfo } from "@/components/invoicing/SectionErrorBoundary";
import { PaidCommissionCard } from "@/components/reports/PaidCommissionCard";
import { PaidSalesCard } from "@/components/reports/PaidSalesCard";
import { NetCommissionCard } from "@/components/reports/NetCommissionCard";
import { isAgencyOwnerRole } from "@/lib/roles";
import { TraveledSalesCard } from "@/components/reports/TraveledSalesCard";
import { TripsTraveledCard } from "@/components/reports/TripsTraveledCard";
import { RevenueSnapshotCard } from "@/components/reports/RevenueSnapshotCard";
import { SalesCommissionChart } from "@/components/reports/SalesCommissionChart";
import { SupplierInsightsCard } from "@/components/reports/SupplierInsightsCard";
import { TopDestinationsCard } from "@/components/reports/TopDestinationsCard";
import { InvoicesByMonthChart } from "@/components/reports/InvoicesByMonthChart";
import { TopClientsCard } from "@/components/reports/TopClientsCard";
import { AdvisorActivityCard } from "@/components/reports/AdvisorActivityCard";
import { SnapshotPrintProvider } from "@/context/SnapshotPrintContext";
import {
  beginSnapshotPrint,
  waitForPrintPaint,
} from "@/lib/snapshotPrint";
import { snapshotPdfName } from "@/lib/pdfFilename";

// ---------------------------------------------------------------------------
// Trip Return Date Filter
// ---------------------------------------------------------------------------

function TripReturnDateFilter({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  onClear,
}: {
  dateFrom: string;
  dateTo: string;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onClear: () => void;
}) {
  const hasFilter = dateFrom || dateTo;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`min-h-11 gap-1.5 md:min-h-0 ${
            hasFilter
              ? "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
              : ""
          }`}
        >
          <CalendarRange className="h-3.5 w-3.5" />
          {hasFilter
            ? `${dateFrom || "..."} – ${dateTo || "..."}`
            : "Trip Return Date"}
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-3">
          <p className="text-sm font-medium">Trip Return Date</p>
          <p className="text-xs text-muted-foreground">
            Filter all reports by trip return date. Leave empty for all-time.
          </p>
          <div className="space-y-1.5">
            <label
              htmlFor="report-date-from"
              className="text-xs text-muted-foreground"
            >
              From
            </label>
            <Input
              id="report-date-from"
              type="date"
              className="h-8 text-xs"
              value={dateFrom}
              onChange={(e) => onDateFromChange(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <label
              htmlFor="report-date-to"
              className="text-xs text-muted-foreground"
            >
              To
            </label>
            <Input
              id="report-date-to"
              type="date"
              className="h-8 text-xs"
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
            />
          </div>
          {hasFilter && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={onClear}
            >
              Clear dates
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Main Reports Page
// ---------------------------------------------------------------------------

export default function Reports() {
  const { user } = useAuth();
  const { impersonation } = useImpersonation();
  const { effectiveRole, hasCompanyAccess } = useCompanyAccess();
  const { data: tramsData, isLoading: tramsLoading } =
    useCurrentTramsContext();
  const rawTramsAccountId = tramsData?.context?.tramsAccountId;

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const printResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (printResetTimerRef.current != null) {
        clearTimeout(printResetTimerRef.current);
      }
    };
  }, []);

  const [viewMode, setViewMode] = useState<"my" | "all">(
    hasCompanyAccess ? "all" : "my",
  );

  useEffect(() => {
    setViewMode(hasCompanyAccess ? "all" : "my");
  }, [hasCompanyAccess]);

  // Team view drops the account (all advisors, all accounts), except for an
  // owner: the server splits a multi-account owner's Team view by entity.
  const currentTramsAccountId =
    viewMode === "all" && !isAgencyOwnerRole(effectiveRole) ? undefined : rawTramsAccountId;
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [snapshotPrinting, setSnapshotPrinting] = useState(false);
  const [printedAt, setPrintedAt] = useState<string | null>(null);

  const handleViewModeChange = useCallback((mode: "my" | "all") => {
    setViewMode(mode);
  }, []);

  const handleClearDates = useCallback(() => {
    setDateFrom("");
    setDateTo("");
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    setIsDownloading(true);
    setSnapshotPrinting(true);
    setPrintedAt(
      new Date().toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      }),
    );
    const endSnapshotPrint = beginSnapshotPrint();

    const previousTitle = document.title;
    document.title = snapshotPdfName(dateFrom, dateTo);

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      document.title = previousTitle;
      if (printResetTimerRef.current != null) {
        clearTimeout(printResetTimerRef.current);
        printResetTimerRef.current = null;
      }
      window.removeEventListener("afterprint", finish);
      endSnapshotPrint();
      if (isMountedRef.current) {
        setSnapshotPrinting(false);
        setPrintedAt(null);
        setIsDownloading(false);
      }
    };

    try {
      await waitForPrintPaint();
      window.print();
    } catch {
      finish();
      return;
    }

    window.addEventListener("afterprint", finish, { once: true });
    if (printResetTimerRef.current != null) {
      clearTimeout(printResetTimerRef.current);
    }
    printResetTimerRef.current = setTimeout(finish, 2000);
  }, [dateFrom, dateTo]);

  const preparedForName = useMemo(() => {
    if (!user) return "—";
    return user.displayName?.trim() || user.email || "—";
  }, [user]);

  const tramsAccountLabel = useMemo(() => {
    const ctx = tramsData?.context;
    if (!ctx) return null;
    return `${ctx.agentName} (${ctx.interfaceId})`;
  }, [tramsData?.context]);

  const scopeContext = useMemo<SectionScopeInfo>(
    () => ({
      memberId: impersonation.active ? impersonation.targetUserId ?? user?.id : user?.id,
      tramsAccountId: currentTramsAccountId,
      role: effectiveRole ?? undefined,
      viewMode,
    }),
    [impersonation.active, impersonation.targetUserId, user?.id, effectiveRole, currentTramsAccountId, viewMode],
  );

  // Full-page loading state
  if (tramsLoading) {
    return (
      <div
        className="p-4 sm:p-6 max-w-[1600px] mx-auto space-y-6"
        aria-label="Loading reports"
      >
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-56" />
          <div className="flex gap-3">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-44" />
            <Skeleton className="h-9 w-36" />
          </div>
        </div>
        <div className="flex gap-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 w-[260px] rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-56 rounded-xl" />
        <Skeleton className="h-72 rounded-xl" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  return (
    <SectionScopeContext.Provider value={scopeContext}>
      <SnapshotPrintProvider active={snapshotPrinting}>
      <div className="p-4 sm:p-6 max-w-[1600px] mx-auto space-y-6 print:p-4 print:space-y-4 print:max-w-full print:w-full print:box-border">
        {/* ---------------------------------------------------------------- */}
        {/* Page Header & Controls                                           */}
        {/* ---------------------------------------------------------------- */}
        <div className="flex flex-col gap-4 print:hidden">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                Snapshot
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {viewMode === "all"
                  ? "Team Report"
                  : "Personal Report"}
                {(dateFrom || dateTo) && (
                  <span className="ml-1">
                    &middot; {dateFrom || "Start"} – {dateTo || "Present"}
                  </span>
                )}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Team / Personal toggle */}
              {hasCompanyAccess && (
                <div
                  className="flex rounded-md bg-muted p-0.5"
                  role="tablist"
                  aria-label="Report scope"
                >
                  <button
                    role="tab"
                    aria-selected={viewMode === "all"}
                    className={`flex min-h-11 items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors md:min-h-0 ${
                      viewMode === "all"
                        ? "bg-background shadow-sm text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => handleViewModeChange("all")}
                  >
                    <Users className="h-3.5 w-3.5" aria-hidden />
                    Team
                  </button>
                  <button
                    role="tab"
                    aria-selected={viewMode === "my"}
                    className={`flex min-h-11 items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors md:min-h-0 ${
                      viewMode === "my"
                        ? "bg-background shadow-sm text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    onClick={() => handleViewModeChange("my")}
                  >
                    <User className="h-3.5 w-3.5" aria-hidden />
                    Personal
                  </button>
                </div>
              )}

              <TramsAccountSwitcher disabled={viewMode === "all" && !isAgencyOwnerRole(effectiveRole)} />

              <TripReturnDateFilter
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
                onClear={handleClearDates}
              />

              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadPdf}
                disabled={isDownloading}
                className="min-h-11 gap-1.5 md:min-h-0"
              >
                {isDownloading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Download PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Print-only cover block — report context for the PDF */}
        <div className="hidden print:block print:mb-6 print:pb-4 print:border-b snapshot-print-section">
          <h1 className="text-2xl font-bold tracking-tight">Financial Snapshot</h1>
          <p className="text-sm text-gray-600 mt-1">
            {viewMode === "all" ? "Team Report" : "Personal Report"}
          </p>
          <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div>
              <dt className="text-gray-500">Prepared for</dt>
              <dd className="font-medium">{preparedForName}</dd>
            </div>
            {viewMode === "my" && tramsAccountLabel && (
              <div>
                <dt className="text-gray-500">TRAMS account</dt>
                <dd className="font-medium">{tramsAccountLabel}</dd>
              </div>
            )}
            {viewMode === "all" && (
              <div>
                <dt className="text-gray-500">Scope</dt>
                <dd className="font-medium">All team advisors</dd>
              </div>
            )}
            <div>
              <dt className="text-gray-500">Trip return date filter</dt>
              <dd className="font-medium">
                {dateFrom || dateTo
                  ? `${dateFrom || "Start"} – ${dateTo || "Present"}`
                  : "Year to date (calendar year)"}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Generated</dt>
              <dd className="font-medium">{printedAt ?? "—"}</dd>
            </div>
          </dl>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* KPI Summary Row                                                  */}
        {/* ---------------------------------------------------------------- */}
        <SectionErrorBoundary sectionName="KPI Summary">
          <section aria-labelledby="kpi-summary-heading" className="snapshot-print-section">
            <h2 id="kpi-summary-heading" className="sr-only">
              Key Performance Indicators
            </h2>
            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 print:grid-cols-3 ${
                isAgencyOwnerRole(effectiveRole) ? "xl:grid-cols-5" : "xl:grid-cols-4"
              }`}
            >
              <SectionErrorBoundary sectionName="YTD Projected Sales">
                <TraveledSalesCard
                  tramsAccountId={currentTramsAccountId}
                  viewMode={viewMode}
                  returnDateFrom={dateFrom || undefined}
                  returnDateTo={dateTo || undefined}
                />
              </SectionErrorBoundary>
              <SectionErrorBoundary sectionName="YTD Paid Sales">
                <PaidSalesCard
                  tramsAccountId={currentTramsAccountId}
                  viewMode={viewMode}
                  returnDateFrom={dateFrom || undefined}
                  returnDateTo={dateTo || undefined}
                />
              </SectionErrorBoundary>
              <SectionErrorBoundary sectionName="YTD GEM Qualifying Commission">
                <PaidCommissionCard
                  tramsAccountId={currentTramsAccountId}
                  viewMode={viewMode}
                  returnDateFrom={dateFrom || undefined}
                  returnDateTo={dateTo || undefined}
                />
              </SectionErrorBoundary>
              {/* Net YTD commission (TRAMS Agent Commission field) — Agency Owners only */}
              {isAgencyOwnerRole(effectiveRole) && (
                <SectionErrorBoundary sectionName="YTD Net Paid Commission">
                  <NetCommissionCard
                    tramsAccountId={currentTramsAccountId}
                    viewMode={viewMode}
                    returnDateFrom={dateFrom || undefined}
                    returnDateTo={dateTo || undefined}
                  />
                </SectionErrorBoundary>
              )}
              <SectionErrorBoundary sectionName="Trips Traveled & Invoiced">
                <TripsTraveledCard
                  tramsAccountId={currentTramsAccountId}
                  viewMode={viewMode}
                  returnDateFrom={dateFrom || undefined}
                  returnDateTo={dateTo || undefined}
                />
              </SectionErrorBoundary>
            </div>
          </section>
        </SectionErrorBoundary>

        {/* ---------------------------------------------------------------- */}
        {/* Advisor Activity Log (Owner/Manager only, Team view only)         */}
        {/* ---------------------------------------------------------------- */}
        {hasCompanyAccess && viewMode === "all" && (
          <SectionErrorBoundary sectionName="Advisor Activity Log">
            <div className="snapshot-print-section snapshot-print-section-breakable">
            <AdvisorActivityCard
              viewMode={viewMode}
              returnDateFrom={dateFrom || undefined}
              returnDateTo={dateTo || undefined}
            />
            </div>
          </SectionErrorBoundary>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* AIVIA Recommends — hidden placeholder, re-enable when ready     */}
        {/* ---------------------------------------------------------------- */}
        {/* TODO(AIVIA-reports-recommends): Skeleton is intentional placeholder—wire SectionErrorBoundary content to recommendation logic + API once spec exists (tracked for discoverability alongside "AIVIA Recommends" header). */}
        {/* <SectionErrorBoundary sectionName="AIVIA Recommends">
          <div className="rounded-xl border border-orange-200 dark:border-orange-800/40 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20 p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="h-8 w-8 rounded-lg bg-orange-100 dark:bg-orange-900/50 flex items-center justify-center">
                  <Sparkles className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-orange-900 dark:text-orange-100">
                  AIVIA Recommends
                </p>
                <Skeleton className="h-4 w-full max-w-[480px] mt-1.5" />
              </div>
            </div>
          </div>
        </SectionErrorBoundary> */}

        {/* ---------------------------------------------------------------- */}
        {/* Sales + Commission Chart (full-width)                            */}
        {/* ---------------------------------------------------------------- */}
        <SectionErrorBoundary sectionName="Sales + Commission Chart">
          <div className="snapshot-print-page-break-before">
            <SalesCommissionChart
              tramsAccountId={currentTramsAccountId}
              viewMode={viewMode}
              userRole={effectiveRole}
              returnDateFrom={dateFrom || undefined}
              returnDateTo={dateTo || undefined}
            />
          </div>
        </SectionErrorBoundary>

        {/* ---------------------------------------------------------------- */}
        {/* Revenue Snapshot + Invoices by Month (side-by-side)              */}
        {/* ---------------------------------------------------------------- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:flex print:flex-col print:gap-4">
          <SectionErrorBoundary sectionName="Revenue Snapshot">
            <RevenueSnapshotCard
              tramsAccountId={currentTramsAccountId}
              viewMode={viewMode}
              returnDateFrom={dateFrom || undefined}
              returnDateTo={dateTo || undefined}
            />
          </SectionErrorBoundary>

          <div className="snapshot-print-page-break-before">
            <SectionErrorBoundary sectionName="Invoices by Month">
              <InvoicesByMonthChart
                tramsAccountId={currentTramsAccountId}
                viewMode={viewMode}
                returnDateFrom={dateFrom || undefined}
                returnDateTo={dateTo || undefined}
              />
            </SectionErrorBoundary>
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Destinations + Clients (left) | Supplier Insights (right)      */}
        {/* ---------------------------------------------------------------- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch print:flex print:flex-col print:gap-4">
          <div className="flex flex-col gap-6 print:gap-4 print:contents">
            <SectionErrorBoundary sectionName="Top Destinations">
              <TopDestinationsCard
                tramsAccountId={currentTramsAccountId}
                viewMode={viewMode}
                userRole={effectiveRole}
                returnDateFrom={dateFrom || undefined}
                returnDateTo={dateTo || undefined}
              />
            </SectionErrorBoundary>

            <SectionErrorBoundary sectionName="Top Clients">
              <TopClientsCard
                tramsAccountId={currentTramsAccountId}
                viewMode={viewMode}
                userRole={effectiveRole}
                returnDateFrom={dateFrom || undefined}
                returnDateTo={dateTo || undefined}
              />
            </SectionErrorBoundary>
          </div>

          <SectionErrorBoundary sectionName="Supplier Insights">
            <SupplierInsightsCard
              tramsAccountId={currentTramsAccountId}
              viewMode={viewMode}
              userRole={effectiveRole}
              returnDateFrom={dateFrom || undefined}
              returnDateTo={dateTo || undefined}
            />
          </SectionErrorBoundary>
        </div>
      </div>
      </SnapshotPrintProvider>
    </SectionScopeContext.Provider>
  );
}
